import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { X, Pencil, Check, KeyRound, Plus, ChevronDown, Loader2, ArrowUpRight } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import type { AdminUser, AdminUserWorkspace, AdminMember } from '../types/index.js'
import { getUserWorkspaces } from '../services/adminApi.js'

interface Props {
  user: AdminUser | null
  onClose: () => void
  onUpdateUser: (
    id: string,
    payload: Partial<{ name: string; email: string; platformRole: 'user' | 'super_admin'; isActive: boolean }>,
  ) => Promise<void>
  onResetPassword: (userId: string, payload: { password: string; confirm_password: string }) => Promise<void>
  onAddToWorkspace: (
    userId: string,
    payload: { workspaceId: string; role: 'owner' | 'admin' | 'member' },
  ) => Promise<AdminMember>
  onUpdateMembershipRole: (
    userId: string,
    membershipId: string,
    role: 'owner' | 'admin' | 'member',
  ) => Promise<AdminMember>
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return iso
  }
}


const WorkspaceSkeleton: React.FC<{ border: string }> = ({ border }) => (
  <div className="py-3 space-y-2">
    <div className="h-3 rounded-full animate-pulse w-1/3" style={{ backgroundColor: border }} />
    <div className="h-3 rounded-full animate-pulse w-1/4" style={{ backgroundColor: border }} />
  </div>
)

const UserDetailPanel: React.FC<Props> = ({
  user,
  onClose,
  onUpdateUser,
  onResetPassword,
  onAddToWorkspace,
  onUpdateMembershipRole,
}) => {
  const { currentTheme } = useTheme()
  const navigate = useNavigate()
  const [workspaces, setWorkspaces] = useState<AdminUserWorkspace[]>([])
  const [workspacesLoading, setWorkspacesLoading] = useState(false)
  const [roleLoading, setRoleLoading] = useState(false)
  const [statusLoading, setStatusLoading] = useState(false)

  // Edit basic info state
  const [editMode, setEditMode] = useState(false)
  const [editName, setEditName] = useState('')
  const [editEmail, setEditEmail] = useState('')
  const [editLoading, setEditLoading] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)

  // Reset password state
  const [resetExpanded, setResetExpanded] = useState(false)
  const [resetPassword, setResetPassword] = useState('')
  const [resetConfirm, setResetConfirm] = useState('')
  const [resetLoading, setResetLoading] = useState(false)
  const [resetError, setResetError] = useState<string | null>(null)
  const [resetSuccess, setResetSuccess] = useState(false)

  // Add to workspace state
  const [addWsExpanded, setAddWsExpanded] = useState(false)
  const [addWsId, setAddWsId] = useState('')
  const [addWsRole, setAddWsRole] = useState<'owner' | 'admin' | 'member'>('member')
  const [addWsLoading, setAddWsLoading] = useState(false)
  const [addWsError, setAddWsError] = useState<string | null>(null)

  // Per-membership loading states
  const [membershipRoleLoading, setMembershipRoleLoading] = useState<Record<string, boolean>>({})

  const isOpen = user !== null

  const fetchWorkspaces = useCallback(async (id: string) => {
    setWorkspacesLoading(true)
    try {
      const result = await getUserWorkspaces(id)
      setWorkspaces(result)
    } catch {
      setWorkspaces([])
    } finally {
      setWorkspacesLoading(false)
    }
  }, [])

  useEffect(() => {
    if (user) {
      setWorkspaces([])
      setEditMode(false)
      setEditError(null)
      setResetExpanded(false)
      setResetPassword('')
      setResetConfirm('')
      setResetError(null)
      setResetSuccess(false)
      setAddWsExpanded(false)
      setAddWsId('')
      setAddWsRole('member')
      setAddWsError(null)
      void fetchWorkspaces(user.id)
    }
  }, [user, fetchWorkspaces])

  // Sync edit fields when user changes
  useEffect(() => {
    if (user) {
      setEditName(user.name)
      setEditEmail(user.email)
    }
  }, [user])

  const handleToggleRole = useCallback(async () => {
    if (!user) return
    setRoleLoading(true)
    try {
      await onUpdateUser(user.id, {
        platformRole: user.platformRole === 'super_admin' ? 'user' : 'super_admin',
      })
    } finally {
      setRoleLoading(false)
    }
  }, [user, onUpdateUser])

  const handleToggleStatus = useCallback(async () => {
    if (!user) return
    setStatusLoading(true)
    try {
      await onUpdateUser(user.id, { isActive: !user.isActive })
    } finally {
      setStatusLoading(false)
    }
  }, [user, onUpdateUser])

  const handleSaveInfo = useCallback(async () => {
    if (!user) return
    setEditLoading(true)
    setEditError(null)
    try {
      await onUpdateUser(user.id, { name: editName.trim(), email: editEmail.trim() })
      setEditMode(false)
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to update user')
    } finally {
      setEditLoading(false)
    }
  }, [user, editName, editEmail, onUpdateUser])

  const handleResetPassword = useCallback(async () => {
    if (!user) return
    setResetLoading(true)
    setResetError(null)
    setResetSuccess(false)
    try {
      await onResetPassword(user.id, { password: resetPassword, confirm_password: resetConfirm })
      setResetSuccess(true)
      setResetPassword('')
      setResetConfirm('')
    } catch (err) {
      setResetError(err instanceof Error ? err.message : 'Failed to reset password')
    } finally {
      setResetLoading(false)
    }
  }, [user, resetPassword, resetConfirm, onResetPassword])

  const handleAddToWorkspace = useCallback(async () => {
    if (!user || !addWsId.trim()) return
    setAddWsLoading(true)
    setAddWsError(null)
    try {
      const newMember = await onAddToWorkspace(user.id, { workspaceId: addWsId.trim(), role: addWsRole })
      // Add the new workspace row to local state
      const newWs: AdminUserWorkspace = {
        membershipId: newMember.membershipId,
        workspaceId: addWsId.trim(),
        workspaceName: addWsId.trim(),
        role: newMember.role,
        joinedAt: newMember.joinedAt,
        isActive: true,
      }
      setWorkspaces((prev) => [...prev, newWs])
      setAddWsExpanded(false)
      setAddWsId('')
      setAddWsRole('member')
    } catch (err) {
      setAddWsError(err instanceof Error ? err.message : 'Failed to add to workspace')
    } finally {
      setAddWsLoading(false)
    }
  }, [user, addWsId, addWsRole, onAddToWorkspace])

  const handleGoToWorkspace = useCallback(
    (workspaceId: string) => {
      onClose()
      navigate(`/admin/workspaces?ws=${workspaceId}`)
    },
    [navigate, onClose],
  )

  const handleUpdateMembershipRole = useCallback(
    async (membershipId: string, role: 'owner' | 'admin' | 'member') => {
      if (!user) return
      setMembershipRoleLoading((prev) => ({ ...prev, [membershipId]: true }))
      try {
        await onUpdateMembershipRole(user.id, membershipId, role)
        setWorkspaces((prev) =>
          prev.map((ws) => (ws.membershipId === membershipId ? { ...ws, role } : ws)),
        )
      } catch {
        // silently keep previous role on error
      } finally {
        setMembershipRoleLoading((prev) => ({ ...prev, [membershipId]: false }))
      }
    },
    [user, onUpdateMembershipRole],
  )

  const inputClass = 'rounded-xl border px-3 py-2 text-sm outline-none w-full transition'
  const inputStyle = {
    backgroundColor: currentTheme.inputBackground,
    color: currentTheme.text,
    borderColor: currentTheme.border,
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 transition-opacity duration-300"
        style={{
          backgroundColor: isOpen ? 'rgba(0,0,0,0.4)' : 'transparent',
          pointerEvents: isOpen ? 'auto' : 'none',
        }}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className="fixed top-0 right-0 h-full z-50 flex flex-col transition-transform duration-300 w-full sm:w-[420px]"
        style={{
          backgroundColor: currentTheme.primary,
          borderLeft: `1px solid ${currentTheme.border}`,
          boxShadow: currentTheme.boxShadow,
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        {user && (
          <>
            {/* Header */}
            <div
              className="flex items-start justify-between px-5 py-4 border-b flex-shrink-0"
              style={{ borderColor: currentTheme.border }}
            >
              <div className="flex-1 min-w-0 pr-3">
                <h2
                  className="text-base font-semibold truncate"
                  style={{ color: currentTheme.text }}
                >
                  {user.name}
                </h2>
                <span
                  className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                  style={
                    user.platformRole === 'super_admin'
                      ? { backgroundColor: `${currentTheme.accent}22`, color: currentTheme.accent }
                      : { backgroundColor: currentTheme.border, color: currentTheme.secondrytext }
                  }
                >
                  {user.platformRole === 'super_admin' ? 'Super Admin' : 'User'}
                </span>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-lg transition"
                style={{ color: currentTheme.secondrytext }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = currentTheme.hoverBackground
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'
                }}
                aria-label="Close panel"
              >
                <X size={16} />
              </button>
            </div>

            {/* Scrollable body */}
            <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-5">

              {/* Section 1: User Info */}
              <div
                className="rounded-xl p-4 space-y-3"
                style={{ backgroundColor: currentTheme.secondary }}
              >
                <div className="flex items-center justify-between">
                  <p
                    className="text-xs font-semibold uppercase tracking-wide"
                    style={{ color: currentTheme.secondrytext }}
                  >
                    User Info
                  </p>
                  {!editMode ? (
                    <button
                      type="button"
                      onClick={() => { setEditMode(true); setEditError(null) }}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition"
                      style={{
                        backgroundColor: `${currentTheme.accent}22`,
                        color: currentTheme.accent,
                      }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.backgroundColor = currentTheme.hoverBackground
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLButtonElement).style.backgroundColor = `${currentTheme.accent}22`
                      }}
                    >
                      <Pencil size={11} />
                      Edit
                    </button>
                  ) : (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => { setEditMode(false); setEditError(null); setEditName(user.name); setEditEmail(user.email) }}
                        className="inline-flex items-center justify-center w-7 h-7 rounded-lg transition"
                        style={{ color: currentTheme.secondrytext }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.backgroundColor = currentTheme.hoverBackground
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'
                        }}
                        aria-label="Cancel edit"
                      >
                        <X size={13} />
                      </button>
                    </div>
                  )}
                </div>

                {editMode ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs mb-1" style={{ color: currentTheme.secondrytext }}>
                        Name
                      </label>
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className={inputClass}
                        style={inputStyle}
                        placeholder="Full name"
                      />
                    </div>
                    <div>
                      <label className="block text-xs mb-1" style={{ color: currentTheme.secondrytext }}>
                        Email
                      </label>
                      <input
                        type="email"
                        value={editEmail}
                        onChange={(e) => setEditEmail(e.target.value)}
                        className={inputClass}
                        style={inputStyle}
                        placeholder="Email address"
                      />
                    </div>
                    {editError && (
                      <div
                        className="rounded-xl px-3 py-2 text-xs"
                        style={{
                          backgroundColor: currentTheme.errorBackground,
                          color: currentTheme.errorText,
                        }}
                      >
                        {editError}
                      </div>
                    )}
                    <div className="flex items-center gap-2 pt-1">
                      <button
                        type="button"
                        disabled={editLoading || !editName.trim() || !editEmail.trim()}
                        onClick={handleSaveInfo}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          backgroundColor: `${currentTheme.accent}22`,
                          color: currentTheme.accent,
                        }}
                      >
                        {editLoading ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <Check size={12} />
                        )}
                        Save Changes
                      </button>
                      <button
                        type="button"
                        onClick={() => { setEditMode(false); setEditError(null); setEditName(user.name); setEditEmail(user.email) }}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition"
                        style={{
                          color: currentTheme.secondrytext,
                          backgroundColor: currentTheme.hoverBackground,
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 text-sm">
                    <div>
                      <span style={{ color: currentTheme.secondrytext }}>Name</span>
                      <p className="mt-0.5 font-medium truncate" style={{ color: currentTheme.text }}>
                        {user.name}
                      </p>
                    </div>
                    <div>
                      <span style={{ color: currentTheme.secondrytext }}>Email</span>
                      <p className="mt-0.5 font-medium truncate" style={{ color: currentTheme.text }}>
                        {user.email}
                      </p>
                    </div>
                    <div>
                      <span style={{ color: currentTheme.secondrytext }}>Platform Role</span>
                      <p className="mt-0.5 font-medium" style={{ color: currentTheme.text }}>
                        {user.platformRole === 'super_admin' ? 'Super Admin' : 'User'}
                      </p>
                    </div>
                    <div>
                      <span style={{ color: currentTheme.secondrytext }}>Account Status</span>
                      <p className="mt-0.5">
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
                          style={
                            user.isActive
                              ? { backgroundColor: currentTheme.successBackground, color: currentTheme.successText }
                              : { backgroundColor: currentTheme.errorBackground, color: currentTheme.errorText }
                          }
                        >
                          {user.isActive ? 'Active' : 'Suspended'}
                        </span>
                      </p>
                    </div>
                    <div>
                      <span style={{ color: currentTheme.secondrytext }}>Member since</span>
                      <p className="mt-0.5 font-medium" style={{ color: currentTheme.text }}>
                        {formatDate(user.createdAt)}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Section 2: Reset Password */}
              <div
                className="rounded-xl overflow-hidden"
                style={{ backgroundColor: currentTheme.secondary }}
              >
                <button
                  type="button"
                  onClick={() => { setResetExpanded((v) => !v); setResetError(null); setResetSuccess(false) }}
                  className="w-full flex items-center justify-between px-4 py-3 text-left transition"
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = currentTheme.hoverBackground
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent'
                  }}
                >
                  <div className="flex items-center gap-2">
                    <KeyRound size={13} style={{ color: currentTheme.secondrytext }} />
                    <p
                      className="text-xs font-semibold uppercase tracking-wide"
                      style={{ color: currentTheme.secondrytext }}
                    >
                      Reset Password
                    </p>
                  </div>
                  <ChevronDown
                    size={14}
                    style={{
                      color: currentTheme.secondrytext,
                      transform: resetExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s',
                    }}
                  />
                </button>

                {resetExpanded && (
                  <div className="px-4 pb-4 space-y-3">
                    <div>
                      <label className="block text-xs mb-1" style={{ color: currentTheme.secondrytext }}>
                        New Password
                      </label>
                      <input
                        type="password"
                        value={resetPassword}
                        onChange={(e) => setResetPassword(e.target.value)}
                        className={inputClass}
                        style={inputStyle}
                        placeholder="Enter new password"
                        autoComplete="new-password"
                      />
                    </div>
                    <div>
                      <label className="block text-xs mb-1" style={{ color: currentTheme.secondrytext }}>
                        Confirm Password
                      </label>
                      <input
                        type="password"
                        value={resetConfirm}
                        onChange={(e) => setResetConfirm(e.target.value)}
                        className={inputClass}
                        style={inputStyle}
                        placeholder="Confirm new password"
                        autoComplete="new-password"
                      />
                    </div>
                    {resetError && (
                      <div
                        className="rounded-xl px-3 py-2 text-xs"
                        style={{
                          backgroundColor: currentTheme.errorBackground,
                          color: currentTheme.errorText,
                        }}
                      >
                        {resetError}
                      </div>
                    )}
                    {resetSuccess && (
                      <div
                        className="rounded-xl px-3 py-2 text-xs font-medium"
                        style={{ backgroundColor: currentTheme.successBackground, color: currentTheme.successText }}
                      >
                        Password updated successfully
                      </div>
                    )}
                    <button
                      type="button"
                      disabled={resetLoading || !resetPassword || !resetConfirm}
                      onClick={handleResetPassword}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        backgroundColor: `${currentTheme.accent}22`,
                        color: currentTheme.accent,
                      }}
                    >
                      {resetLoading ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <KeyRound size={12} />
                      )}
                      Reset Password
                    </button>
                  </div>
                )}
              </div>

              {/* Section 3: Workspaces */}
              <div
                className="rounded-xl p-4"
                style={{ backgroundColor: currentTheme.secondary }}
              >
                <p
                  className="text-xs font-semibold uppercase tracking-wide mb-3"
                  style={{ color: currentTheme.secondrytext }}
                >
                  Workspaces
                </p>

                {workspacesLoading ? (
                  <div className="divide-y" style={{ borderColor: currentTheme.border }}>
                    {Array.from({ length: 3 }).map((_, i) => (
                      <WorkspaceSkeleton key={i} border={currentTheme.border} />
                    ))}
                  </div>
                ) : workspaces.length === 0 ? (
                  <p className="text-sm py-4 text-center" style={{ color: currentTheme.secondrytext }}>
                    Not a member of any workspace.
                  </p>
                ) : (
                  <div className="divide-y" style={{ borderColor: currentTheme.border }}>
                    {workspaces.map((ws) => (
                      <div key={ws.membershipId} className="flex items-start justify-between py-3 gap-3">
                        <div className="flex-1 min-w-0">
                          <button
                            type="button"
                            onClick={() => handleGoToWorkspace(ws.workspaceId)}
                            className="group flex items-center gap-1 text-left max-w-full"
                          >
                            <span
                              className="text-sm font-semibold truncate transition-opacity group-hover:opacity-70"
                              style={{ color: currentTheme.text }}
                            >
                              {ws.workspaceName}
                            </span>
                            <ArrowUpRight
                              size={12}
                              className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                              style={{ color: currentTheme.accent }}
                            />
                          </button>
                          <p className="text-xs mt-0.5" style={{ color: currentTheme.mutedText }}>
                            Joined {formatDate(ws.joinedAt)}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                          {/* Role dropdown */}
                          {membershipRoleLoading[ws.membershipId] ? (
                            <div
                              className="inline-flex items-center justify-center w-7 h-7 rounded-lg"
                              style={{ color: currentTheme.secondrytext }}
                            >
                              <Loader2 size={12} className="animate-spin" />
                            </div>
                          ) : (
                            <select
                              value={ws.role}
                              onChange={(e) =>
                                void handleUpdateMembershipRole(
                                  ws.membershipId,
                                  e.target.value as 'owner' | 'admin' | 'member',
                                )
                              }
                              className="rounded-lg border px-2 py-1 text-xs font-semibold outline-none cursor-pointer capitalize"
                              style={{
                                backgroundColor: currentTheme.inputBackground,
                                color: currentTheme.text,
                                borderColor: currentTheme.border,
                              }}
                            >
                              <option value="owner">owner</option>
                              <option value="admin">admin</option>
                              <option value="member">member</option>
                            </select>
                          )}
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
                            style={
                              ws.isActive
                                ? { backgroundColor: currentTheme.successBackground, color: currentTheme.successText }
                                : { backgroundColor: currentTheme.errorBackground, color: currentTheme.errorText }
                            }
                          >
                            {ws.isActive ? 'Active' : 'Suspended'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add to Workspace */}
                {!addWsExpanded ? (
                  <button
                    type="button"
                    onClick={() => { setAddWsExpanded(true); setAddWsError(null) }}
                    className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition w-full justify-center"
                    style={{
                      backgroundColor: `${currentTheme.accent}22`,
                      color: currentTheme.accent,
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.backgroundColor = currentTheme.hoverBackground
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.backgroundColor = `${currentTheme.accent}22`
                    }}
                  >
                    <Plus size={12} />
                    Add to Workspace
                  </button>
                ) : (
                  <div
                    className="mt-3 rounded-xl p-3 space-y-3 border"
                    style={{
                      backgroundColor: currentTheme.inputBackground,
                      borderColor: currentTheme.border,
                    }}
                  >
                    <div>
                      <label className="block text-xs mb-1" style={{ color: currentTheme.secondrytext }}>
                        Workspace ID
                      </label>
                      <input
                        type="text"
                        value={addWsId}
                        onChange={(e) => setAddWsId(e.target.value)}
                        className={inputClass}
                        style={inputStyle}
                        placeholder="Enter workspace ID"
                      />
                    </div>
                    <div>
                      <label className="block text-xs mb-1" style={{ color: currentTheme.secondrytext }}>
                        Role
                      </label>
                      <select
                        value={addWsRole}
                        onChange={(e) => setAddWsRole(e.target.value as 'owner' | 'admin' | 'member')}
                        className={inputClass}
                        style={inputStyle}
                      >
                        <option value="member">member</option>
                        <option value="admin">admin</option>
                        <option value="owner">owner</option>
                      </select>
                    </div>
                    {addWsError && (
                      <div
                        className="rounded-xl px-3 py-2 text-xs"
                        style={{
                          backgroundColor: currentTheme.errorBackground,
                          color: currentTheme.errorText,
                        }}
                      >
                        {addWsError}
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={addWsLoading || !addWsId.trim()}
                        onClick={handleAddToWorkspace}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          backgroundColor: `${currentTheme.accent}22`,
                          color: currentTheme.accent,
                        }}
                      >
                        {addWsLoading ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <Plus size={12} />
                        )}
                        Add
                      </button>
                      <button
                        type="button"
                        onClick={() => { setAddWsExpanded(false); setAddWsId(''); setAddWsRole('member'); setAddWsError(null) }}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition"
                        style={{
                          color: currentTheme.secondrytext,
                          backgroundColor: currentTheme.hoverBackground,
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Actions footer */}
            <div
              className="px-5 py-4 border-t flex-shrink-0 space-y-2"
              style={{ borderColor: currentTheme.border }}
            >
              <button
                type="button"
                disabled={roleLoading}
                onClick={handleToggleRole}
                className="w-full py-2.5 rounded-xl text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: `${currentTheme.accent}22`,
                  color: currentTheme.accent,
                }}
              >
                {roleLoading
                  ? 'Updating...'
                  : user.platformRole === 'super_admin'
                  ? 'Demote to User'
                  : 'Promote to Admin'}
              </button>
              <button
                type="button"
                disabled={statusLoading}
                onClick={handleToggleStatus}
                className="w-full py-2.5 rounded-xl text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                style={
                  user.isActive
                    ? { backgroundColor: currentTheme.errorBackground, color: currentTheme.errorText }
                    : { backgroundColor: currentTheme.successBackground, color: currentTheme.successText }
                }
              >
                {statusLoading
                  ? 'Updating...'
                  : user.isActive
                  ? 'Suspend Account'
                  : 'Activate Account'}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  )
}

export default UserDetailPanel
